import { getCommonAnalytics } from '../common-analytics';

declare global {
  interface Window {
    onVidyardAPI?: (vidyardEmbed: any) => void;
    alreadyTrackedVidyardLightboxPlayers?: any[];
  }
}

export function registerVidyardAnalytics(): void {
  window.onVidyardAPI = (vidyardEmbed: any) => {
    vidyardEmbed.api.addReadyListener((_: any, player: any) => {
      const findElementWithComp = (...elements: any[]): Element | null => {
        for (const element of elements) {
          if (!element) {
            continue;
          }
          if (element.getAttribute && element.getAttribute('data-layer-comp')) {
            return element;
          }
          if (element.closest) {
            const closestElement = element.closest('[data-layer-comp]');
            if (closestElement) {
              return closestElement;
            }
          }
        }
        return null;
      };

      const getVidyardAnalyticsElement = (): Element | null => {
        const playerType = player.placeholder && player.placeholder.dataset ? player.placeholder.dataset.type : null;

        if (playerType === 'lightbox') {
          return findElementWithComp(player.element && player.element.nextSibling);
        }

        const parentElement = player.element ? player.element.parentElement : null;
        if (parentElement && parentElement.dataset.type === 'launch') {
          if (parentElement.classList.contains('isSVGComp')) {
            return findElementWithComp(parentElement);
          }
          return findElementWithComp(parentElement.nextElementSibling, parentElement);
        }

        return findElementWithComp(
          player.placeholder,
          player.element,
          parentElement,
          document.querySelector(`[data-layer-video-id="${player.uuid}"]`),
          document.querySelector(`.vidyard-player-embed[data-uuid="${player.uuid}"]`)
        );
      };

      const buildVidyardBaseData = () => ({
        'video-type': 'Vidyard',
        'video-uuid': player.uuid,
        'video-name': player.metadata.name,
      });

      const emitVideoEvent = (eventName: string, dataToEmit: Record<string, any>): void => {
        if (!dataToEmit.comp) {
          return;
        }
        const finalData = { ...buildVidyardBaseData(), ...dataToEmit };
        window.Mhecomm?.emit(eventName, finalData, {}, player.element);
      };

      const handleVideoEvent = (event: string): void => {
        const eventTime = player.currentTime();
        const completePercentage = Math.floor((player.currentTime() / player.metadata.length_in_seconds) * 100);

        let eventData: Record<string, any> = {};

        if (event === 'video-play') {
          eventData = { 'video-start-time': eventTime };
        } else if (event === 'video-paused') {
          eventData = {
            'video-pause-at-time': eventTime,
            'video-complete-percentage': completePercentage,
          };
        } else if (event === 'video-complete') {
          eventData = { 'video-completion-percentage': 100 };
        } else if (event === 'video-closed') {
          eventData = {
            'video-closed-at-time': eventTime,
            'video-complete-percentage': completePercentage,
          };
        }

        const analyticsElement = getVidyardAnalyticsElement();
        const data = getCommonAnalytics(analyticsElement as Element);
        emitVideoEvent(event, { ...eventData, ...data });
      };

      const startFirstPlayFallbackMonitor = (maxAttempts = 40): void => {
        if (player.__mheFirstPlayFallbackTimer) {
          clearInterval(player.__mheFirstPlayFallbackTimer);
        }

        player.__mhePlaySeenForCurrentOpen = false;
        let attempts = 0;

        player.__mheFirstPlayFallbackTimer = setInterval(() => {
          attempts += 1;

          if (player.__mhePlaySeenForCurrentOpen || typeof player.currentTime !== 'function') {
            clearInterval(player.__mheFirstPlayFallbackTimer);
            player.__mheFirstPlayFallbackTimer = null;
            return;
          }

          if (player.currentTime() > 0) {
            player.__mhePlaySeenForCurrentOpen = true;
            handleVideoEvent('video-play');
            clearInterval(player.__mheFirstPlayFallbackTimer);
            player.__mheFirstPlayFallbackTimer = null;
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(player.__mheFirstPlayFallbackTimer);
            player.__mheFirstPlayFallbackTimer = null;
          }
        }, 250);
      };

      const sendLightboxOpenEventIfNotAlreadyTracked = (): void => {
        window.alreadyTrackedVidyardLightboxPlayers = window.alreadyTrackedVidyardLightboxPlayers || [];
        const playerOpenedByButton = window.alreadyTrackedVidyardLightboxPlayers.includes(player);
        if (playerOpenedByButton) {
          window.alreadyTrackedVidyardLightboxPlayers = window.alreadyTrackedVidyardLightboxPlayers.filter(
            (p) => p !== player
          );
        }
        if (player.placeholder.dataset.type === 'lightbox' && !playerOpenedByButton) {
          const elementLightbox = player.element.nextSibling;
          const finalData = { ...buildVidyardBaseData(), ...getCommonAnalytics(elementLightbox) };

          window.Mhecomm?.emit('video-open', finalData, {}, player.element);
          startFirstPlayFallbackMonitor();
        }
      };

      sendLightboxOpenEventIfNotAlreadyTracked();

      const playerType = player.placeholder && player.placeholder.dataset ? player.placeholder.dataset.type : null;
      if (playerType !== 'lightbox') {
        // Embedded players don't trigger the lightbox-open path, so keep a longer fallback window.
        startFirstPlayFallbackMonitor(1200);
      }

      // Use an explicit marker instead of relying on Vidyard's internal callback store.
      if (!player.__mheAnalyticsListenersBound) {
        player.on('play', () => {
          player.__mhePlaySeenForCurrentOpen = true;
          handleVideoEvent('video-play');
        });
        player.on('pause', () => handleVideoEvent('video-paused'));
        player.on('playerComplete', () => handleVideoEvent('video-complete'));
        player.on('lightboxClose', () => {
          player.__mhePlaySeenForCurrentOpen = false;
          handleVideoEvent('video-closed');
        });
        player.__mheAnalyticsListenersBound = true;
      }
    });
  };
}
